import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import StringResourceManager, { GetString } from '../StringResourceManager';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useDispatch, useSelector } from 'react-redux';
import { createStyles, makeStyles } from '@material-ui/core';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
} from '@progress/kendo-react-grid';
import { DataResult, State, process } from '@progress/kendo-data-query';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { ApplicationState } from '../../store';
import {
  commandStatusIconMap,
  getCommandStatusToEnum,
  getDate,
  getLocalDateTime,
  useChipStyle,
} from '../../utils/utils';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import { CommandHistoryRecord } from '../../Interfaces/Commands/CommandRecordInfo';
import { Chip } from '@progress/kendo-react-buttons';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { commandStatusTextMap } from '../../utils/textMappings';
import { LoadingPanel } from '../LoadingPanel';
import ConfigChangesGrid from '../Config/History/ConfigChangesGrid';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { Divider } from '@mui/material';
import {
  getCommandByBatchID,
  resetNotificationMessage,
} from '../../store/Commands/CommandsAction';

const useStyles = makeStyles(theme =>
  createStyles({
    cancelButton: {
      width: '180px',
      border: '1px solid #000000',
    },
    buttonStyle: {
      alignItems: 'flex-end',
      marginTop: 33,
    },
    modalContainer: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '80vw',
    },
    dialogTitle: {
      display: 'flex',
      flexDirection: 'row',
    },
    selectedLanesView: {
      marginTop: 5,
      marginBottom: 5,
    },
    titleDate: {
      marginLeft: 10,
    },
    doneButtonView: {
      marginTop: 10,
      textAlign: 'center',
    },
    scheduleUploadLogsContainer: {
      margin: 2,
    },
    lineChartHeader: {
      fontSize: 20,
      textDecorationLine: 'underline',
      marginBottom: 10,
      fontWeight: 'bold',
    },
    queryLabel: {
      display: 'flex',
      flexDirection: 'row',
      fontSize: 18,
    },
    queryInput: {
      fontWeight: 'bold',
      fontSize: 18,
      paddingLeft: 5,
    },
    wrapInput: {
      display: 'block',
      maxWidth: '90%',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    },
  }),
);

const CreatedOnCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  return <td>{getLocalDateTime(dataItem.CreatedOn)}</td>;
};

interface Props {
  handleCloseDialog: (open: boolean) => void;
  command: string;
  dropDownFilterCell:
    | React.ComponentType<GridCustomFilterCellProps>
    | undefined;
}

const CommandHistoryDialog: React.FC<Props> = ({
  handleCloseDialog,
  command,
  dropDownFilterCell: dropDownFilterCell,
}): React.ReactElement => {
  const classes = useStyles();

  const initialDataState: State = {
    take: 50,
    skip: 0,
    group: [],
  };

  const dispatch = useDispatch();

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const commandList: CommandHistoryRecord[] = useSelector(
    (state: ApplicationState) => state.commands.commandListByBatchID,
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) => state.commands.isLoadingCommands,
  );

  const message: string = useSelector(
    (state: ApplicationState) => state.commands.notificationMessage,
  );

  const [configChangesDetails, setConfigChangesDetails] = React.useState<any>(
    [],
  );

  const [creationDate, setCreationDate] = React.useState<string>('');
  const [batchId, setBatchId] = React.useState<any>('');

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const extractDates = (str: any) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(str, 'text/xml');

    const startDateElement = xmlDoc.querySelector('StartDate');
    const startDateString = startDateElement
      ? startDateElement.textContent
      : null;
    const startDate = startDateString ? new Date(startDateString) : null;

    const endDateElement = xmlDoc.querySelector('EndDate');
    const endDateString = endDateElement ? endDateElement.textContent : null;
    const endDate = endDateString ? new Date(endDateString) : null;

    return {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    };
  };

  const getCommandDataRecord = (commandData: CommandHistoryRecord[]) => {
    let result: CommandHistoryRecord[] = [];

    if (Array.isArray(commandData)) {
      commandData.forEach(commandRecord => {
        let range = extractDates(commandRecord.Parameters);
        let data: CommandHistoryRecord = {
          ID: commandRecord.ID,
          Uuid: commandRecord.Uuid,
          NodeInfo: commandRecord.NodeInfo,
          Status: commandRecord.Status,
          CreatedOn: commandRecord.CreatedOn,
          StartDate: range.startDate,
          EndDate: range.endDate,
          CommandBatchID: commandRecord.CommandBatchID,
        };
        result.push(data);
      });
    }

    return result;
  };

  const processWithGroups = (data: any, dataState: State): DataResult => {
    const newDataState = process(data, dataState);

    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };
  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(getCommandDataRecord(commandList), dataState),
  );

  const newData = setExpandedState({
    data: resultState.data,
    collapsedIds: collapsedState,
  });

  React.useEffect(() => {
    const processedCommandData = getCommandDataRecord(commandList);
    setResultState(processWithGroups(processedCommandData, dataState));
    if (commandList.length > 0) {
      setBatchId(commandList[0].CommandBatchID);
    }

    if (command == 'ApplyConfig') {
      if (commandList.length > 0 && commandList[0].Parameters != undefined) {
        setConfigChangesDetails(JSON.parse(commandList[0].Parameters));
        let createdDate: any = commandList[0].CreatedOn;
        setCreationDate(getLocalDateTime(createdDate));
      }
    }
  }, [commandList]);

  const dispatchGetCommandByBatchId = () => {
    getCommandByBatchID(dispatch, command, batchId);
  };

  React.useEffect(() => {
    let timeoutId: any;
    if (message !== '') {
      dispatchGetCommandByBatchId();
      timeoutId = setTimeout(() => {
        resetNotificationMessage(dispatch);
      });
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [message]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(
      getCommandDataRecord(commandList),
      event.dataState,
    );
    setResultState(newDataState);
    setDataState(event.dataState);
  };

  const COLUMN_TITLE_STORE = GetString('COLUMN_TITLE_STORE');
  const COLUMN_TITLE_CLUSTER = GetString('COLUMN_TITLE_CLUSTER');
  const COLUMN_TITLE_LANE = GetString('COLUMN_TITLE_LANE');
  const COLUMN_TITLE_DIVISION = GetString('COLUMN_TITLE_DIVISION');
  const COLUMN_TITLE_STATUS = GetString('COLUMN_TITLE_STATUS');
  const COLUMN_TITLE_REQUEST_DATE = GetString('COLUMN_REQUEST_DATE');
  const COLUMN_TITLE_START_DATE = GetString('COLUMN_LOGS_START_DATE');
  const COLUMN_TITLE_END_DATE = GetString('COLUMN_LOGS_END_DATE');

  const requestDateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CreatedOn"
      />
    );
  };

  const startDateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="StartDate"
      />
    );
  };

  const endDateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="EndDate"
      />
    );
  };

  const LogsStartDateCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    return <td>{getDate(dataItem.StartDate)}</td>;
  };

  const LogsEndDateCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;

    return <td>{getDate(dataItem.EndDate)}</td>;
  };

  const StatusCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;

    const status: any = getCommandStatusToEnum(dataItem.Status.toString());

    const commandStatus: any = commandStatusTextMap(status);

    let chipStyle: any = useChipStyle(status);

    if (status) {
      return (
        <td>
          <Chip
            className={chipStyle().root}
            text={GetString(commandStatus)}
            svgIcon={commandStatusIconMap(status)}
          />
        </td>
      );
    }
    return null;
  };

  return (
    <Dialog
      title={
        <div className={classes.dialogTitle}>
          <StringResourceManager
            variant="body1"
            style="commandLabel"
            stringKey={
              command == 'ApplyConfig'
                ? 'CONFIG_CHANGE_REQUEST'
                : command == 'ImportItems'
                  ? 'NBC_REQUEST_HISTORY'
                  : 'LOGS_REQUEST_HISTORY'
            }
            gutterBottom={true}
          />
          <Label className={classes.titleDate}>{creationDate}</Label>
        </div>
      }
      onClose={handleClose}
    >
      <div className={classes.modalContainer}>
        {loading && LoadingPanel}
        {configChangesDetails.length > 0 ? (
          <div>
            <div>
              <StringResourceManager
                variant="body1"
                style="dialogLabel"
                stringKey={
                  configChangesDetails[0].Category != undefined
                    ? 'SETTING_CHANGES'
                    : 'FEATURE_CHANGES'
                }
              />
            </div>
            <div>
              <ConfigChangesGrid configChanges={configChangesDetails} />
            </div>
          </div>
        ) : null}
        <Divider />
        <div>
          <div className={classes.selectedLanesView}>
            <StringResourceManager
              variant="body1"
              style="dialogLabel"
              stringKey={'SELECTED_LANES'}
              gutterBottom={true}
            />
          </div>
          <div>
            <Grid
              style={{ height: '300px' }}
              data={resultState}
              {...dataState}
              resizable={true}
              sortable={true}
              filterable={true}
              pageable={{
                buttonCount: 4,
                pageSizes: [100, 500, 1000, 5000],
              }}
              reorderable={true}
              fixedScroll={false}
              onDataStateChange={dataStateChange}
            >
              <GridColumn
                field="NodeInfo.Division"
                title={COLUMN_TITLE_DIVISION}
                width="160px"
              />
              <GridColumn
                field="NodeInfo.SiteNumber"
                title={COLUMN_TITLE_STORE}
                width="160px"
              />
              <GridColumn
                field="NodeInfo.NodeGroup"
                title={COLUMN_TITLE_CLUSTER}
                width="160px"
              />
              <GridColumn
                field="NodeInfo.LaneNumber"
                title={COLUMN_TITLE_LANE}
                width="160px"
              />
              <GridColumn
                field="Status"
                title={COLUMN_TITLE_STATUS}
                width="160px"
                cells={{ data: StatusCell, filterCell: dropDownFilterCell }}
              />
              <GridColumn
                field="CreatedOn"
                title={COLUMN_TITLE_REQUEST_DATE}
                width="200px"
                cells={{
                  data: CreatedOnCell,
                  filterCell: requestDateFilterCell,
                }}
              />
              {command == 'UploadLogs' ? (
                <GridColumn
                  field="StartDate"
                  title={COLUMN_TITLE_START_DATE}
                  width="220px"
                  cells={{
                    data: LogsStartDateCell,
                    filterCell: startDateFilterCell,
                  }}
                />
              ) : null}
              {command == 'UploadLogs' ? (
                <GridColumn
                  field="EndDate"
                  title={COLUMN_TITLE_END_DATE}
                  width="220px"
                  cells={{
                    data: LogsEndDateCell,
                    filterCell: endDateFilterCell,
                  }}
                />
              ) : null}
            </Grid>
          </div>
          <div className={classes.doneButtonView}>
            <Button themeColor={'primary'} type="button" onClick={handleClose}>
              {GetString('DONE')}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default CommandHistoryDialog;
